import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

import './HoverTooltip.css';

const VIEWPORT_MARGIN = 8;
const GAP = 8;
// Long enough for the pointer to cross the gap between the trigger and an `interactive` panel.
const CLOSE_DELAY_MS = 150;

const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

const HoverTooltip = ({
    children,
    content,
    className = '',
    panelClassName = '',
    maxWidth = 360,
    disabled = false,
    placement = 'auto',
    interactive = false,
}) => {
    const anchorRef = useRef(null);
    const popRef = useRef(null);
    const closeTimer = useRef(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);

    useEffect(() => () => clearTimeout(closeTimer.current), []);

    // The panel is portaled but stays inside the anchor in React's tree, so entering it counts as entering the anchor.
    const show = () => {
        clearTimeout(closeTimer.current);
        setOpen(true);
    };
    const hide = () => {
        clearTimeout(closeTimer.current);
        if (interactive) closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
        else setOpen(false);
    };

    const shown = open && !disabled && !!content;

    const place = useCallback(() => {
        const anchor = anchorRef.current;
        const pop = popRef.current;
        if (!anchor || !pop) return;
        const r = anchor.getBoundingClientRect();
        const { clientWidth: vw, clientHeight: vh } = document.documentElement;
        const w = pop.offsetWidth;
        const h = pop.offsetHeight;

        // 'side': beside the anchor (right, else left), top-aligned - for anchors stacked in a column, such as
        // menu items, where above / below would cover the neighbouring item.
        if (placement === 'side') {
            const fitsRight = r.right + GAP + w <= vw - VIEWPORT_MARGIN;
            const sideLeft = clamp(fitsRight ? r.right + GAP : r.left - GAP - w, VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - w);
            const sideTop = clamp(r.top, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - h);
            setPos(prev => (prev?.left === sideLeft && prev?.top === sideTop ? prev : { left: sideLeft, top: sideTop }));
            return;
        }

        // The panel's size never depends on where it sits (see the style below), so near the right edge it
        // slides left at its full width instead of squeezing into the space that is left and wrapping.
        const left = clamp(r.left, VIEWPORT_MARGIN, vw - VIEWPORT_MARGIN - w);

        // The preferred side if the panel fits there, else the other side if it fits there, else the roomier
        // side, slid back onto the screen by the clamp, over the trigger if it has to be. Never a scrollbar.
        const roomBelow = vh - VIEWPORT_MARGIN - r.bottom - GAP;
        const roomAbove = r.top - GAP - VIEWPORT_MARGIN;
        const fitsBelow = h <= roomBelow;
        const fitsAbove = h <= roomAbove;
        const aboveIsRoomier = roomAbove > roomBelow;
        const up = placement === 'top'
            ? fitsAbove || (!fitsBelow && aboveIsRoomier)
            : !fitsBelow && (fitsAbove || aboveIsRoomier);
        const top = clamp(up ? r.top - GAP - h : r.bottom + GAP, VIEWPORT_MARGIN, vh - VIEWPORT_MARGIN - h);

        setPos(prev => (prev?.left === left && prev?.top === top ? prev : { left, top }));
    }, [placement]);

    useLayoutEffect(() => {
        if (!shown) { setPos(null); return undefined; }
        place();
        // A panel that changes size after it was placed (content swapped while open, a flag image or a font
        // arriving) is placed again, as it is on scroll and resize.
        const observer = new ResizeObserver(place);
        observer.observe(popRef.current);
        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);
        return () => {
            observer.disconnect();
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [shown, place]);

    if (disabled || !content) {
        return <span className={`hover-tip-anchor ${className}`}>{children}</span>;
    }

    const widthCap = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth;

    return (
        <span
            ref={anchorRef}
            className={`hover-tip-anchor ${className}`}
            onMouseEnter={show}
            onMouseLeave={hide}
        >
            {children}
            {/* Mounted hidden for one measuring pass, then shown in place before the browser paints. Its
                width is its content's, capped at maxWidth (or the screen, if that is narrower), so text
                shorter than the cap stays on one line and anything longer wraps at the cap. */}
            {shown && createPortal(
                <div
                    ref={popRef}
                    role={interactive ? undefined : 'tooltip'}
                    className={`hover-tip-pop ${panelClassName}`}
                    style={{
                        position: 'fixed',
                        left: pos?.left ?? 0,
                        top: pos?.top ?? 0,
                        width: 'max-content',
                        maxWidth: `min(${widthCap}, calc(100% - ${VIEWPORT_MARGIN * 2}px))`,
                        maxHeight: `calc(100% - ${VIEWPORT_MARGIN * 2}px)`,
                        visibility: pos ? 'visible' : 'hidden',
                    }}
                >
                    {content}
                </div>,
                document.body
            )}
        </span>
    );
};

export default HoverTooltip;
